import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { ChannelPeriodType, ChannelTrendPoint } from '@/hooks/useEvents'
import { fmtAxisDate, fmtDate, fmtMonth } from '@/lib/dates'

// Channel counterpart to WeeklyTrendChart. A channel has no pack list, so
// there's no sell-through to pair with revenue — one panel, one axis. Same
// palette as the event chart: burgundy is identity, not status, and a channel
// has no selected period, so every bar is full --brand.
const BRAND = 'var(--brand)'
const GRID = 'var(--line-warm)'
const MUTE = 'var(--mute)'

const money = (v: number) => `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

// Past two weeks of bars (e.g. a 30-day view) the value labels collide, so
// they only show at this count or fewer; the tooltip still has every figure.
const MAX_LABELLED_BARS = 14

export function ChannelTrendChart({
  trend,
  label,
  periodType,
  filtersActive = false,
}: {
  trend: ChannelTrendPoint[]
  label: string
  periodType: ChannelPeriodType
  // The trend comes off the channel's whole-day totals, so it never narrows
  // with the product filters the table below obeys. Say so rather than let
  // the two disagree silently.
  filtersActive?: boolean
}) {
  if (trend.length < 2) return null
  const isMonth = periodType === 'month'
  // Day buckets are already full ISO dates — same tick as a week, minus the
  // "Week of" prefix, which would misread a single day as a whole week.
  const isDay = periodType === 'day'
  const noun = isMonth ? 'month' : isDay ? 'day' : 'week'
  const tickLabel = (v: unknown) => (isMonth ? fmtMonth(String(v)) : fmtAxisDate(String(v)))
  const axisProps = {
    tick: { fontSize: 10, fill: MUTE },
    stroke: GRID,
    tickLine: false,
  }
  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 mb-3">
      <h2 className="text-sm font-semibold mb-2">Trend by {noun}</h2>
      <div className="text-xs text-[var(--mute)] mb-1">
        Revenue by {noun} — {label}
        {filtersActive && ' · All products — not affected by filters'}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        {/* Top margin leaves room for the value labels above the tallest bar. */}
        <BarChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="period_start" {...axisProps} tickFormatter={tickLabel} />
          <YAxis {...axisProps} tickFormatter={money} width={56} />
          <Tooltip
            contentStyle={{
              background: 'var(--surface-card)',
              border: '1px solid var(--line-warm)',
              borderRadius: 2,
              fontSize: 11,
              color: 'var(--text-primary)',
            }}
            labelStyle={{ color: 'var(--mute)' }}
            cursor={{ fill: 'var(--surface-header)' }}
            formatter={(value, _name, item) => [
              `${money(Number(value))} · ${item?.payload?.units_sold ?? 0} units`,
              'Revenue',
            ]}
            labelFormatter={(v) =>
              isMonth || isDay
                ? (isMonth ? fmtMonth(String(v)) : fmtDate(String(v)))
                : `Week of ${fmtDate(String(v))}`
            }
          />
          <Bar dataKey="revenue" fill={BRAND} radius={[4, 4, 0, 0]}>
            {trend.length <= MAX_LABELLED_BARS && (
              <LabelList
                dataKey="revenue"
                position="top"
                formatter={(v) => money(Number(v))}
                style={{ fontSize: 10, fill: 'var(--ink-2)' }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
